/**
 * Team / Employee Signup — INVITE-ONLY
 * URL: /team/join?invite=<code>
 * Landing without a valid invite shows a friendly "you need an invite" screen
 * with a mailto to super-admin.
 */

import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, ShieldCheck, ArrowLeft, CheckCircle, Users,
  Sparkles, MailPlus, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/App";

const API = process.env.REACT_APP_BACKEND_URL;
const STERILE_MATE_LOGO = "/images/sterilemate-logo.jpg";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function TeamJoinPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const inviteCode = params.get("invite") || "";

  const [inviteInfo, setInviteInfo] = useState(null); // {valid, role_label, invited_by, invited_email, reason}
  const [inviteChecked, setInviteChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    linkedin_url: "",
    password: "",
    confirm: "",
  });

  // Validate the invite code
  useEffect(() => {
    if (!inviteCode) { setInviteChecked(true); return; }
    fetch(`${API}/api/team/invites/${encodeURIComponent(inviteCode)}/validate`)
      .then(r => r.json())
      .then(d => {
        setInviteInfo(d);
        if (d.valid && d.invited_email) {
          setForm(f => ({ ...f, email: d.invited_email }));
        }
      })
      .catch(() => setInviteInfo({ valid: false, reason: "network" }))
      .finally(() => setInviteChecked(true));
  }, [inviteCode]);

  const on = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) return toast.error("Full name is required");
    if (!emailRe.test(form.email)) return toast.error("Please enter a valid work email");
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    if (form.password !== form.confirm) return toast.error("Passwords don't match");

    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        linkedin_url: form.linkedin_url.trim() || null,
        password: form.password,
        invite_code: inviteCode,
      };
      const res = await fetch(`${API}/api/team/register-self`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Registration failed");
      setSuccess(data);
      toast.success("Welcome aboard — you're signed in!");
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const goToPortal = () => {
    if (success?.token && success?.user) {
      login(success.token, success.user);
    }
    navigate("/admin");
  };

  // --- Success card ----------------------------------------------------
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 flex items-center justify-center p-6">
        <div
          className="bg-white rounded-2xl shadow-2xl border border-teal-200 max-w-xl w-full p-8 text-center"
          data-testid="team-signup-success-card"
        >
          <img src={STERILE_MATE_LOGO} alt="Sterile Mate" className="h-14 mx-auto mb-4" />
          <CheckCircle className="w-16 h-16 text-teal-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">You&apos;re in!</h1>
          <p className="text-slate-600 mb-4">
            Welcome to the Sterile Mate team &mdash; your dashboard is ready.
          </p>
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 text-sm text-teal-900 text-left space-y-2 mb-5">
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-teal-700 mt-0.5 shrink-0" />
              <span>Signed in as <strong>{success.email}</strong>. Your password is set.</span>
            </div>
            <div className="flex items-start gap-2">
              <Users className="w-4 h-4 text-teal-700 mt-0.5 shrink-0" />
              <span>Role: <strong>{inviteInfo?.role_label || success.role}</strong></span>
            </div>
          </div>
          <Button
            onClick={goToPortal}
            className="bg-teal-600 hover:bg-teal-700 text-white font-bold h-11 px-6"
            data-testid="team-goto-portal"
          >
            Go to my dashboard
          </Button>
        </div>
      </div>
    );
  }

  // --- No invite / invalid invite state --------------------------------
  if (inviteChecked && (!inviteCode || !inviteInfo?.valid)) {
    const reason = inviteInfo?.reason;
    const reasonMsg =
      reason === "expired" ? "This invite link has expired." :
      reason === "already_used" ? "This invite link has already been used." :
      reason === "not_found" ? "This invite link is invalid." :
      !inviteCode ? "The Sterile Mate team portal is invite-only." :
      "This invite link is no longer valid.";
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div
          className="bg-white rounded-2xl shadow-xl border border-slate-200 max-w-lg w-full p-8 text-center"
          data-testid="team-invite-required-card"
        >
          <img src={STERILE_MATE_LOGO} alt="Sterile Mate" className="h-14 mx-auto mb-4" />
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
            <Lock className="w-7 h-7 text-amber-700" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Invite required</h1>
          <p className="text-slate-600 mb-6">
            {reasonMsg} Access is granted only via an invite from a Sterile Mate administrator.
          </p>
          <a
            href="mailto:george.broughton@sterilemate.com?subject=Sterile%20Mate%20team%20invite%20request&body=Hi%20George%2C%20I%27d%20like%20an%20invite%20to%20the%20Sterile%20Mate%20team%20portal.%20My%20role%3A%20"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm"
            data-testid="team-request-invite-cta"
          >
            <MailPlus className="w-4 h-4" /> Request an invite
          </a>
          <div className="mt-6">
            <Link to="/" className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // --- Form ------------------------------------------------------------
  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <img src={STERILE_MATE_LOGO} alt="Sterile Mate" className="h-14 mx-auto mb-3" />
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-900 text-white rounded-full text-[10px] font-bold tracking-widest uppercase mb-3">
            <Users className="w-3.5 h-3.5" /> Team Portal &middot; Internal
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
            Complete your Sterile Mate account
          </h1>
          <p className="text-slate-600 mt-2">
            Set your password to activate your access &mdash; you&apos;ll be signed in immediately.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 md:p-8 space-y-6"
          data-testid="team-signup-form"
        >
          {/* Invite banner */}
          {inviteInfo?.valid && (
            <div
              className="bg-teal-50 border border-teal-200 rounded-lg px-4 py-3 flex items-start gap-3"
              data-testid="team-invite-valid-banner"
            >
              <Sparkles className="w-5 h-5 text-teal-700 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-bold text-teal-900">
                  You&apos;re invited to join as {inviteInfo.role_label}
                </p>
                <p className="text-xs text-teal-800">
                  Invited by {inviteInfo.invited_by || "your Sterile Mate admin"}.
                  {inviteInfo.invited_email ? ` This invite is for ${inviteInfo.invited_email}.` : ""}
                </p>
              </div>
            </div>
          )}

          {/* Identity */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-teal-700 mb-3">Your Information</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="full_name">Full name *</Label>
                <Input id="full_name" name="full_name" value={form.full_name} onChange={on} required data-testid="team-full-name" placeholder="Jane Doe" />
              </div>
              <div>
                <Label htmlFor="email">Work email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={on}
                  required
                  data-testid="team-email"
                  placeholder="jane@sterilemate.com"
                  readOnly={Boolean(inviteInfo?.invited_email)}
                  className={inviteInfo?.invited_email ? "bg-slate-50" : ""}
                />
                {inviteInfo?.invited_email && (
                  <p className="text-[11px] text-slate-500 mt-1">Locked by your invite.</p>
                )}
              </div>
              <div>
                <Label htmlFor="phone">Phone <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input id="phone" name="phone" value={form.phone} onChange={on} data-testid="team-phone" placeholder="281.456.3458" />
              </div>
              <div>
                <Label htmlFor="linkedin_url">LinkedIn URL <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input id="linkedin_url" name="linkedin_url" value={form.linkedin_url} onChange={on} data-testid="team-linkedin" placeholder="https://linkedin.com/in/..." />
              </div>
            </div>
          </div>

          {/* Password */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-teal-700 mb-3">Set Your Password</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="password">Password *</Label>
                <Input id="password" name="password" type="password" value={form.password} onChange={on} required minLength={8} data-testid="team-password" />
              </div>
              <div>
                <Label htmlFor="confirm">Confirm password *</Label>
                <Input id="confirm" name="confirm" type="password" value={form.confirm} onChange={on} required minLength={8} data-testid="team-confirm" />
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">
              Your account activates instantly on submit &mdash; you&apos;ll be signed in.
            </p>
          </div>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-slate-200">
            <p className="text-xs text-slate-500">
              By submitting, you agree to Sterile Mate&apos;s internal team policies.
            </p>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-teal-600 hover:bg-teal-700 text-white px-6 h-11 font-bold"
              data-testid="team-submit"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Activating…</>
              ) : (
                "Create My Account"
              )}
            </Button>
          </div>
        </form>

        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
